import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, username: form.username.trim(), email: form.email.trim() };
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;
  };

  return (
    <div className="min-h-screen grid-bg grid place-items-center px-6 py-16">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="auth-logo">
          <img src="/logo.png" alt="FR Proxies" className="w-10 h-10 rounded-lg object-contain" />
          <span className="font-head font-extrabold tracking-tight text-xl">FR <span className="text-neon">PROXIES</span></span>
        </Link>
        <div className="led-border bg-surface border border-[#CBEEF5] rounded-xl p-8 glow-cyan">
          <h1 className="font-head text-2xl font-bold mb-1">Create your account</h1>
          <p className="text-sm text-zinc-500 mb-6">Start generating proxies in minutes.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-zinc-600">Name</Label>
              <Input data-testid="register-name-input" value={form.name} onChange={set("name")} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="Your name" />
            </div>
            <div>
              <Label className="text-zinc-600">Username</Label>
              <Input data-testid="register-username-input" required minLength={3} maxLength={20} pattern="[A-Za-z0-9_]+" title="3–20 characters: letters, numbers or underscore" value={form.username} onChange={set("username")} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="3–20 letters, numbers or _" />
              <p className="text-xs text-zinc-500 mt-1">Used for your proxy login credentials.</p>
            </div>
            <div>
              <Label className="text-zinc-600">Email</Label>
              <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-zinc-600">Password</Label>
              <Input data-testid="register-password-input" type="password" required value={form.password} onChange={set("password")} className="mt-1 bg-base border-[#CBEEF5] focus:border-neon" placeholder="min 6 characters" />
            </div>
            <Button data-testid="register-submit-button" disabled={busy} className="w-full bg-neon text-base hover:bg-neon-hover font-semibold">{busy ? "Creating…" : "Create account"}</Button>
          </form>
          <div className="flex items-center gap-3 my-6"><div className="h-px bg-[#262626] flex-1" /><span className="text-xs text-zinc-600 font-mono">OR</span><div className="h-px bg-[#262626] flex-1" /></div>
          <button onClick={google} data-testid="google-register-button" className="w-full border border-[#CBEEF5] bg-base hover:bg-surface2 flex items-center justify-center gap-3 py-3 rounded-md transition-colors text-sm">Continue with Google</button>
          <p className="text-sm text-zinc-500 text-center mt-6">Have an account? <Link to="/login" className="text-neon hover:underline" data-testid="goto-login-link">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
